import { Check, Star, Clock, Layers, CalendarCheck, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ExternalCta } from "@/components/common/ExternalCta";

export const PackageCard = ({ item }) => {
  const featured = !!item.featured;
  return (
    <div
      data-testid="package-card"
      className={cn(
        "relative flex flex-col rounded-xl border bg-card p-6 transition-transform duration-200 hover:-translate-y-1",
        featured
          ? "border-[hsl(var(--brand-red))] border-t-4 shadow-lg shadow-[hsl(var(--brand-red))]/10"
          : "border-border"
      )}
    >
      {featured && (
        <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
          <Star className="h-3 w-3" /> Most Popular
        </div>
      )}

      {item.category && (
        <span
          data-testid="package-category"
          className="self-start rounded-full bg-muted px-3 py-1 font-condensed text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
        >
          {item.category}
        </span>
      )}

      <h3 className="mt-3 display-title text-2xl">{item.title}</h3>

      <div className="mt-2 flex items-baseline gap-2">
        <span className="display-title text-4xl text-brand">{item.price_label}</span>
        {item.billing && <span className="text-sm text-muted-foreground">/ {item.billing}</span>}
      </div>

      {/* Commitment + access level meta */}
      <div className="mt-3 grid gap-1.5 text-xs text-muted-foreground">
        {item.commitment && (
          <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-brand" />{item.commitment}</span>
        )}
        {item.access_level && (
          <span className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-brand" />{item.access_level}</span>
        )}
      </div>

      {item.description && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.description}</p>
      )}

      {Array.isArray(item.features) && item.features.length > 0 && (
        <ul className="mt-5 flex-1 space-y-2">
          {item.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{f}
            </li>
          ))}
        </ul>
      )}

      {/* Primary checkout */}
      <ExternalCta
        href={item.cta_url}
        variant={featured ? "primary" : "secondary"}
        showIcon={false}
        testid="package-checkout-btn"
        trackType="package_click"
        trackLabel={item.title}
        className="mt-6 w-full"
      >
        {item.cta_label || "Checkout"}
      </ExternalCta>

      {/* Secondary actions */}
      <div className="mt-2 flex items-center justify-center gap-4 text-xs">
        {item.book_intro_url && (
          <a
            href={item.book_intro_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="package-book-intro-link"
            className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
          >
            <CalendarCheck className="h-3.5 w-3.5 text-brand" /> Book Intro
          </a>
        )}
        {item.portal_url && (
          <a
            href={item.portal_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="package-portal-link"
            className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
          >
            App / Portal <ArrowUpRight className="h-3.5 w-3.5 text-brand" />
          </a>
        )}
      </div>
    </div>
  );
};
